/**
 * Builds the REAL Activities feed from live data — the non-mock source for ActivitiesPage.
 * The UI is unchanged; this just produces a valid `ActivitiesFeed` (members + events +
 * summaryByDay) from what actually happened: agent runs, workflow runs, pending approvals, and
 * the agent-to-agent message board (comms). Everything else (day grouping, lanes, timeline,
 * quick access) is derived client-side from these events.
 */
import {
  fetchAgents,
  fetchAgentRuns,
  fetchWorkflowRuns,
  fetchAllowances,
  fetchAgentBoard,
  type Agent,
} from "~/morrowAgents";
import { dayStripAround, eventsForDay, localDayKey, summarizeDay } from "./groupActivitiesByDay";
import type {
  ActivitiesFeed,
  ActivityEvent,
  ActivityMember,
  ActivityStatus,
} from "./activitiesTypes";

const SYSTEM_MEMBER: ActivityMember = {
  userId: "system",
  name: "Automation",
  title: "System",
  avatarSeed: "morrow-system",
};

/** Map the assorted backend status strings onto the UI's ActivityStatus enum. */
function toStatus(raw: string | null | undefined): ActivityStatus {
  const s = (raw ?? "").toLowerCase();
  if (s === "running" || s === "starting") return "running";
  if (s === "error" || s === "failed") return "failed";
  if (s === "pending" || s === "queued") return "pending";
  if (s === "denied") return "denied";
  if (s === "approved") return "approved";
  // ok / test / done / completed / anything settled
  return "completed";
}

/** A short human phrase for a run's source/detail (e.g. "scheduled", "pursue", a cron). */
function runLabel(source: string, detail: string | null): string {
  const d = (detail ?? "").trim();
  if (d) return d;
  const map: Record<string, string> = {
    schedule: "Scheduled wake",
    scheduled: "Scheduled wake",
    wake: "Self-wake",
    pursue: "Pursuing a goal",
    trigger: "Triggered",
    chat: "From chat",
    manual: "Run now",
    test: "Test run",
  };
  return map[source] ?? source ?? "Run";
}

/** Build an ActivityMember from an agent (or the system fallback). */
function memberFor(agentId: string | null, agents: Map<string, Agent>): ActivityMember {
  if (!agentId) return SYSTEM_MEMBER;
  const a = agents.get(agentId);
  if (!a) return { ...SYSTEM_MEMBER, userId: agentId, name: "Agent", avatarSeed: agentId };
  return {
    userId: a.id,
    name: a.name,
    title: a.role || "Agent",
    ...(a.role ? { role: a.role } : {}),
    avatarSeed: a.id,
  };
}

/** Fetch everything in parallel and assemble the feed. */
export async function buildActivitiesFeed(): Promise<ActivitiesFeed> {
  const [agentList, agentRuns, workflowRuns, allowances, board] = await Promise.all([
    fetchAgents(),
    fetchAgentRuns(),
    fetchWorkflowRuns(),
    fetchAllowances(),
    fetchAgentBoard(),
  ]);

  const agents = new Map<string, Agent>(agentList.map((a) => [a.id, a]));
  const todayIso = new Date().toISOString();
  const events: ActivityEvent[] = [];

  for (const r of agentRuns) {
    const actor = memberFor(r.agentId, agents);
    events.push({
      id: `run_${r.id}`,
      orgId: "personal",
      actor,
      kind: "agent_run",
      status: toStatus(r.status),
      title: actor.name,
      summary: runLabel(r.source, r.detail) + (r.error ? ` — ${r.error}` : ""),
      refs: {
        agentId: r.agentId,
        agentLabel: actor.name,
        ...(r.threadId ? { threadId: r.threadId } : {}),
      },
      occurredAt: r.createdAt,
      dueAt: null,
    });
  }

  for (const r of workflowRuns) {
    events.push({
      id: `wfrun_${r.id}`,
      orgId: "personal",
      actor: SYSTEM_MEMBER,
      kind: "workflow_run",
      status: toStatus(r.status),
      title: "Workflow run",
      summary: runLabel(r.source, r.detail) + (r.error ? ` — ${r.error}` : ""),
      refs: {
        workflowId: r.workflowId,
        runId: r.id,
        ...(r.threadId ? { threadId: r.threadId } : {}),
      },
      occurredAt: r.createdAt,
      dueAt: null,
    });
  }

  for (const a of allowances) {
    events.push({
      id: `allow_${a.id}`,
      orgId: "personal",
      actor: SYSTEM_MEMBER,
      kind: "allowance",
      status: "needs_approval",
      title: a.title || "Approval needed",
      summary: a.action,
      refs: { allowanceId: a.id, ...(a.threadId ? { threadId: a.threadId } : {}) },
      occurredAt: a.createdAt,
      // A pending approval still needs action NOW, so surface it on today's To-do lane (via dueAt)
      // regardless of when it was raised — eventsForDay matches occurredAt OR dueAt to the day.
      dueAt: todayIso,
    });
  }

  for (const m of board) {
    const actor = memberFor(m.fromAgentId, agents);
    const toLabel = m.toAgentId ? memberFor(m.toAgentId, agents).name : "everyone";
    events.push({
      id: `board_${m.id}`,
      orgId: "personal",
      actor,
      kind: "comms",
      status: "completed",
      title: m.subject || `${actor.name} → ${toLabel}`,
      summary: m.body,
      refs: {
        agentId: m.fromAgentId,
        agentLabel: actor.name,
        // Keep the recipient structured so the card/detail can show the real from→to flow.
        ...(m.toAgentId
          ? { toAgentId: m.toAgentId, toAgentLabel: toLabel }
          : { toAgentLabel: "everyone" }),
      },
      occurredAt: m.createdAt,
      dueAt: null,
    });
  }

  // Distinct actors → filter options.
  const memberMap = new Map<string, ActivityMember>();
  for (const e of events)
    if (!memberMap.has(e.actor.userId)) memberMap.set(e.actor.userId, e.actor);

  // Per-day counts across the visible strip (±45 days around today), same as the mock feed.
  const summaryByDay: Record<string, ReturnType<typeof summarizeDay>> = {};
  for (const key of dayStripAround(new Date())) {
    summaryByDay[key] = summarizeDay(eventsForDay(events, key));
  }
  // Also cover any event days outside that window so their counts aren't lost.
  for (const e of events) {
    const key = localDayKey(new Date(e.occurredAt));
    if (!summaryByDay[key]) summaryByDay[key] = summarizeDay(eventsForDay(events, key));
  }

  return { members: [...memberMap.values()], events, summaryByDay };
}
